import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Mail, CheckCircle, XCircle, Loader2, RefreshCw } from 'lucide-react';
import { authAPI } from '../services/api';
import useUserStore from '../store/userStore';

// Page shown after registration - prompts user to check email
export const VerifyEmailNoticePage = () => {
  const { user, logout } = useUserStore();
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleResend = async () => {
    if (!user?.email || resendCooldown > 0) return;

    setIsResending(true);
    try {
      await authAPI.resendVerification(user.email);
      toast.success('Verification email sent! Please check your inbox.');
      setResendCooldown(60); // 60 second cooldown
    } catch (error) {
      toast.error('Failed to resend verification email. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">
        <div className="bg-slate-800/50 rounded-2xl shadow-xl border border-slate-700 p-8 text-center">
          <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Mail className="w-10 h-10 text-blue-600" />
          </div>

          <h1 className="text-2xl font-bold text-white mb-2">
            Check Your Email
          </h1>

          <p className="text-slate-400 mb-6">
            We've sent a verification link to{' '}
            <span className="font-medium text-white">{user?.email || 'your email'}</span>.
            Please click the link to verify your account.
          </p>

          <div className="bg-slate-700/50 rounded-lg p-4 mb-6 text-left">
            <h3 className="font-medium text-white mb-2">Didn't receive the email?</h3>
            <ul className="text-sm text-slate-400 space-y-1">
              <li>- Check your spam or junk folder</li>
              <li>- Make sure the email address is correct</li>
              <li>- Wait a few minutes and try again</li>
            </ul>
          </div>

          <button
            onClick={handleResend}
            disabled={isResending || resendCooldown > 0}
            className="w-full flex justify-center items-center py-3 px-4 border border-slate-600 rounded-lg shadow-sm text-sm font-medium text-slate-300 bg-slate-700 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all mb-4"
          >
            {isResending ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Sending...
              </>
            ) : resendCooldown > 0 ? (
              <>
                <RefreshCw className="w-5 h-5 mr-2" />
                Resend in {resendCooldown}s
              </>
            ) : (
              <>
                <RefreshCw className="w-5 h-5 mr-2" />
                Resend Verification Email
              </>
            )}
          </button>

          <Link
            to="/"
            className="block w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-medium hover:from-blue-700 hover:to-purple-700 transition-all mb-4"
          >
            Continue to App
          </Link>

          <button
            onClick={logout}
            className="text-sm text-slate-400 hover:text-slate-300 transition-colors"
          >
            Sign out and use a different account
          </button>
        </div>
      </div>
    </div>
  );
};

// Page that handles the verification token from email link
export const VerifyEmailTokenPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser, setEmailVerified } = useUserStore();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [message, setMessage] = useState('');

  useEffect(() => {
    const verifyToken = async () => {
      const token = searchParams.get('token');

      if (!token) {
        setStatus('error');
        setMessage('Invalid verification link. Please request a new one.');
        return;
      }

      try {
        const response = await authAPI.verifyEmail(token);
        const { user, token: newToken, message } = response.data;

        // Update user state with verified status
        if (user && newToken) {
          setUser(user, newToken);
        }
        setEmailVerified(true);

        setStatus('success');
        setMessage(message || 'Email verified successfully!');

        // Redirect to home after 3 seconds
        setTimeout(() => navigate('/'), 3000);
      } catch (error) {
        setStatus('error');
        setMessage(error.response?.data?.error || 'Verification failed. Please try again.');
      }
    };

    verifyToken();
  }, [searchParams, navigate, setUser, setEmailVerified]);

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">
        <div className="bg-slate-800/50 rounded-2xl shadow-xl border border-slate-700 p-8 text-center">
          {status === 'loading' && (
            <>
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">
                Verifying Your Email
              </h1>
              <p className="text-slate-400">
                Please wait while we verify your email address...
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">
                Email Verified!
              </h1>
              <p className="text-slate-400 mb-6">
                {message}
              </p>
              <p className="text-sm text-slate-400 mb-4">
                Redirecting to dashboard...
              </p>
              <Link
                to="/"
                className="inline-block py-3 px-6 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-medium hover:from-blue-700 hover:to-purple-700 transition-all"
              >
                Go to Dashboard
              </Link>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <XCircle className="w-10 h-10 text-red-600" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">
                Verification Failed
              </h1>
              <p className="text-slate-400 mb-6">
                {message}
              </p>
              <div className="space-y-3">
                <Link
                  to="/login"
                  className="block w-full py-3 px-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-medium hover:from-blue-700 hover:to-purple-700 transition-all"
                >
                  Go to Login
                </Link>
                <Link
                  to="/register"
                  className="block w-full py-3 px-4 border border-slate-600 rounded-lg font-medium text-slate-300 hover:bg-slate-700 transition-all"
                >
                  Create New Account
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailTokenPage;
